import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';
import Currency from '../../components/currency/Currency';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../components/HoverCard';
import { useFirmContext } from '../../providers/FirmProvider';
import { useWorkpaperContext } from '../../providers/WorkpaperProvider';
import { WorksheetWithAccount } from '../../types/worksheet';
import { isNullOrUndefined } from '../new-workpaper/utils';

type ClosingAmountDiffWarningProps = {
    worksheet: WorksheetWithAccount;
};

function ClosingAmountDiffWarning({ worksheet }: ClosingAmountDiffWarningProps): JSX.Element | null {
    if (!worksheet.priorWorksheetId || !worksheet.priorClosingAmount) {
        return null;
    }

    if (
        worksheet.priorClosingAmount.debit === worksheet.account.ledgerDebitLY &&
        worksheet.priorClosingAmount.credit === worksheet.account.ledgerCreditLY
    ) {
        return null;
    }

    return (
        <HoverCard>
            <HoverCardTrigger>
                <ExclamationTriangleIcon className="h-4 w-4 text-red-600" />
            </HoverCardTrigger>
            <HoverCardContent>
                <div onClick={(e): void => e.stopPropagation()}>
                    {worksheet.priorWorksheetId && <PriorWorksheetDetails worksheet={worksheet} />}
                </div>
            </HoverCardContent>
        </HoverCard>
    );
}

type PriorWorksheetDetailsProps = {
    worksheet: WorksheetWithAccount;
};
function PriorWorksheetDetails({ worksheet }: PriorWorksheetDetailsProps): JSX.Element {
    const { shortId } = useFirmContext();
    const workpaper = useWorkpaperContext();
    return (
        <div className="cursor-default text-sm text-contentSecondary text-left">
            <p className="text-sm text-contentPrimary font-bold">Difference between periods</p>
            <p className="text-xs mt-1">
                The closing balance from the prior period&apos;s worksheet doesn&apos;t match the comparative amount on this worksheet.
            </p>
            <hr className="my-2" />
            <p>
                Comparative amount
                <br />
                <b>
                    <Currency
                        value={worksheet.account.ledgerDebitLY ?? worksheet.account.ledgerCreditLY}
                        withParentheses={!isNullOrUndefined(worksheet.account.ledgerCreditLY)}
                    />
                </b>
            </p>
            <p className="mt-2">
                Closing amount
                <br />
                <b>
                    <Currency
                        value={worksheet.priorClosingAmount?.debit ?? worksheet.priorClosingAmount?.credit}
                        withParentheses={!isNullOrUndefined(worksheet.priorClosingAmount?.credit)}
                    />
                </b>
            </p>
            <hr className="my-2" />
            <Link
                to={`/${shortId}/workpapers/${workpaper.priorWorkpaperId}/trial-balance/${worksheet.priorWorksheetId}`}
                className="text-primary mt-2 block font-medium"
            >
                See prior worksheet
            </Link>
        </div>
    );
}

export default ClosingAmountDiffWarning;
